import { useMemo } from "react";
import { extractAirportIata } from "@/helpers/bookingHelpers";
import { FlightStatus } from "@/redux/apis/clientFlight/types";
import { useAppSelector } from "@/redux/hooks";
import {
  selectPickup,
  selectPickupDatetime,
  selectPickupFlightDelayMinutes,
  selectPickupFlightFrozenReason,
  selectPickupFlightNumber,
  selectViewOnly,
} from "@/redux/slices/clientBooking/selectors";
import { flightDateFromPickup, isFlightPickupFrozen } from "../flightPickup";
import { useFlightDetails } from "./useFlightDetails";

/**
 * The booked flight's live status, for surfaces that show a BADGE rather than a time.
 *
 * ## It must not cost a request
 *
 * Every argument is built exactly as {@link useSheetFlightPickup} builds it — same `flightDateFromPickup`
 * off the committed pickup and wait, same `extractAirportIata`, same skips — so RTK Query serialises them
 * identically and this joins the entry the sheet already holds. `ClientBooking` mounts that hook for the
 * whole sheet, so by the time any summary renders, the entry exists. Deriving the date any other way (the
 * pickup's own day, say) forks the cache key and spends a Cirium lookup to show a badge.
 *
 * The two skips are copied for the same reason and not because a badge needs them: a VIEW-ONLY booking and
 * a FROZEN pickup are both cases the sheet hook declines to fetch, and fetching here would be a request
 * that surface never made before. Neither loses anything — a pickup the engine will no longer move has no
 * "your flight is early, and so is your car" story to tell.
 *
 * The flight-tracking add-on gate lives inside `useFlightDetails`, so an operator without it resolves no
 * flight and every caller renders nothing.
 */
export const useBookedFlightStatus = (): { status: FlightStatus; delay: number } | null => {
  const pickup = useAppSelector(selectPickup);
  const pickupFlightNumber = useAppSelector(selectPickupFlightNumber);
  const committed = useAppSelector(selectPickupDatetime);
  const committedDelay = useAppSelector(selectPickupFlightDelayMinutes);
  const frozenReason = useAppSelector(selectPickupFlightFrozenReason);
  const viewOnly = useAppSelector(selectViewOnly);

  const flightDate = useMemo(
    () => (committed && pickupFlightNumber ? flightDateFromPickup(committed, committedDelay) : null),
    [committed, pickupFlightNumber, committedDelay]
  );

  const { currentData: flight, isError } = useFlightDetails({
    flightNumber: pickupFlightNumber ?? "",
    date: flightDate ?? "",
    airport: extractAirportIata(pickup) ?? undefined,
    skip: !pickupFlightNumber || !flightDate || viewOnly || isFlightPickupFrozen(frozenReason),
  });

  // `not_found` and `unknown` are the lookup failing to say anything, and "Flight Pending" beside a booked
  // date is noise dressed as information. Every other status — including an ordinary on-time one — is a
  // fact about the flight this booking is pinned to, which is what the badge is there to carry.
  // `currentData` and `isError`, matching every sibling reader of this query. `data` retains the PREVIOUS
  // arguments' result, so between "QF2 typed" and "QF2 resolved" it still holds QF1424's — the summary
  // would badge the old flight's status under the new number. And RTK Query keeps the last good `data`
  // through a failed refetch, so a badge built on it goes on asserting a status the server has stopped
  // confirming. See `useSheetFlightPickup`, which reads `currentData` for the first reason, and
  // `DateTimePanel`, which gates on `isError` for the second.
  if (isError || !flight || flight.status === "not_found" || flight.status === "unknown") return null;
  return { status: flight.status, delay: flight.delay };
};
